import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import {
  Plug,
  Check,
  X,
  Settings,
  ExternalLink,
  Mail,
  MessageSquare,
  CreditCard,
  BarChart3,
  Cloud,
  Lock,
  Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';

const integrations = [
  {
    id: 'google-analytics',
    name: 'Google Analytics',
    description: 'Track website traffic and user behavior',
    icon: BarChart3,
    color: 'bg-orange-500',
    category: 'Analytics',
    popular: true,
    plan: 'starter'
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Accept payments online',
    icon: CreditCard,
    color: 'bg-purple-600',
    category: 'Payment',
    popular: true,
    plan: 'free'
  },
  {
    id: 'paypal',
    name: 'PayPal',
    description: 'PayPal payment integration',
    icon: CreditCard,
    color: 'bg-blue-600',
    category: 'Payment',
    popular: false,
    plan: 'free'
  },
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    description: 'Email marketing automation',
    icon: Mail,
    color: 'bg-yellow-500',
    category: 'Marketing',
    popular: true,
    plan: 'professional'
  },
  {
    id: 'sendgrid',
    name: 'SendGrid',
    description: 'Transactional email service',
    icon: Mail,
    color: 'bg-blue-500',
    category: 'Marketing',
    popular: false,
    plan: 'professional'
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Team communication',
    icon: MessageSquare,
    color: 'bg-green-500',
    category: 'Communication',
    popular: false,
    plan: 'starter'
  },
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Connect apps and automate workflows',
    icon: Plug,
    color: 'bg-orange-600',
    category: 'Automation',
    popular: true,
    plan: 'professional'
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    description: 'CDN and security',
    icon: Cloud,
    color: 'bg-orange-400',
    category: 'Infrastructure',
    popular: false,
    plan: 'enterprise'
  }
];

const Integrations = () => {
  const { user } = useAuth();
  const { subscription, PLAN_LIMITS } = useSubscription();
  const [connected, setConnected] = useState(['stripe']);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const categories = ['all', ...new Set(integrations.map(i => i.category))];

  const canAccessIntegration = (integration) => {
    const planOrder = ['free', 'starter', 'professional', 'enterprise'];
    const userPlanIndex = planOrder.indexOf(subscription?.plan || 'free');
    const requiredPlanIndex = planOrder.indexOf(integration.plan);
    return userPlanIndex >= requiredPlanIndex;
  };

  const handleConnect = (integration) => {
    if (!canAccessIntegration(integration)) {
      toast.error(`This integration requires ${integration.plan} plan or higher`);
      return;
    }

    if (connected.includes(integration.id)) {
      setConnected(connected.filter(id => id !== integration.id));
      toast.success(`${integration.name} disconnected`);
    } else {
      setSelectedIntegration(integration);
      setShowConfigModal(true);
    }
  };

  const handleConfigure = () => {
    setConnected([...connected, selectedIntegration.id]);
    toast.success(`${selectedIntegration.name} connected successfully!`);
    setShowConfigModal(false);
    setSelectedIntegration(null);
  };

  const filteredIntegrations = selectedCategory === 'all' 
    ? integrations 
    : integrations.filter(i => i.category === selectedCategory);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
          <p className="text-gray-500">Connect your favorite tools and services</p>
        </div>
        <div className="text-sm text-gray-500">
          {connected.length} of {integrations.length} connected
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map(category => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              selectedCategory === category
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredIntegrations.map((integration) => {
          const isConnected = connected.includes(integration.id);
          const hasAccess = canAccessIntegration(integration);

          return (
            <div
              key={integration.id}
              className={`bg-white rounded-xl shadow-sm border-2 overflow-hidden transition-all ${
                isConnected ? 'border-green-500' : 'border-gray-200'
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 ${integration.color} rounded-xl flex items-center justify-center`}>
                    <integration.icon className="w-6 h-6 text-white" />
                  </div>
                  {isConnected && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                      <Check className="w-3 h-3" />
                      Connected
                    </span>
                  )}
                  {!hasAccess && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                      <Lock className="w-3 h-3" />
                      {integration.plan}
                    </span>
                  )}
                </div>

                <h3 className="font-semibold text-gray-900 mb-1">{integration.name}</h3>
                <p className="text-sm text-gray-500 mb-4">{integration.description}</p>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{integration.category}</span>
                  <button
                    onClick={() => handleConnect(integration)}
                    disabled={!hasAccess}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isConnected
                        ? 'bg-red-50 text-red-600 hover:bg-red-100'
                        : hasAccess
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isConnected ? 'Disconnect' : 'Connect'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Coming Soon */}
      <div className="bg-gradient-to-r from-primary-50 to-purple-50 rounded-xl p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">More integrations coming soon</h3>
            <p className="text-sm text-gray-500">
              We're constantly adding new integrations. Let us know what you'd like to see!
            </p>
          </div>
        </div>
      </div>

      {/* Config Modal */}
      {showConfigModal && selectedIntegration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-12 h-12 ${selectedIntegration.color} rounded-xl flex items-center justify-center`}>
                <selectedIntegration.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Connect {selectedIntegration.name}
                </h3>
                <p className="text-sm text-gray-500">Configure your integration settings</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key
                </label>
                <input
                  type="text"
                  placeholder="Enter your API key"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook URL (Optional)
                </label>
                <input
                  type="text"
                  placeholder="https://..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfigModal(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfigure}
                className="flex-1 py-2 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
              >
                Connect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Integrations;
